// messageModal — modal de mensagem reutilizável (substitui window.alert).
//
// Mobile-first: reaproveita as classes de `styles/components/modal.css`, que
// já viram um bottom sheet em telas ≤ 600px. Componente genérico — NÃO
// conhece domínio; serve pra qualquer aviso de uma só ação ("Entendi").
//
// Uso:
//   import { messageModal } from '../../shared/components/messageModal.js';
//   await messageModal({
//     tone: 'info',
//     title: 'Viagem em geração',
//     message: 'A IA ainda está montando o roteiro…',
//   });
//
// Retorna uma Promise que resolve quando o usuário fecha o modal (botão,
// clique no fundo ou Esc). Depende de `modal.css` estar carregado na página
// (todas as páginas do app carregam) e de `primitives.css` (classe .btn).

// Ícone (SVG stroke) por tom. currentColor herda a cor do tom.
const TONE_ICONS = {
  info: '<circle cx="12" cy="12" r="9"/><path d="M12 7.5v5l3 2"/>',
  danger: '<path d="M10.3 3.7 1.8 18a2 2 0 0 0 1.7 3h17a2 2 0 0 0 1.7-3L13.7 3.7a2 2 0 0 0-3.4 0Z"/><path d="M12 9v4"/><path d="M12 16.5v.5"/>',
  success: '<circle cx="12" cy="12" r="9"/><path d="M8.5 12.5l2.5 2.5 4.5-5"/>',
};

// Cor de destaque e fundo do ícone por tom (CSS vars dos tokens do app).
const TONE_COLORS = {
  info:    { fg: 'var(--accent, var(--brand))', bg: 'var(--accent-light, var(--brand-light))' },
  danger:  { fg: 'var(--danger)',  bg: 'var(--danger-light)' },
  success: { fg: 'var(--success)', bg: 'var(--success-light)' },
};

function esc(s) {
  return String(s ?? '')
    .replace(/&/g, '&amp;').replace(/</g, '&lt;')
    .replace(/>/g, '&gt;').replace(/"/g, '&quot;');
}

/**
 * Abre um modal de mensagem com um único botão de ação.
 *
 * @param {object}  opts
 * @param {string}  opts.title         título do modal
 * @param {string}  opts.message       corpo (quebras de linha `\n` são respeitadas)
 * @param {'info'|'danger'|'success'} [opts.tone='info']  cor/ícone
 * @param {string}  [opts.icon]        SVG path custom (sobrepõe o ícone do tom)
 * @param {number}  [opts.progress]    0..1 — renderiza uma barra de progresso
 * @param {string}  [opts.confirmLabel='Entendi']  texto do botão
 * @param {boolean} [opts.dismissible=true]  permite fechar no fundo / Esc
 * @returns {Promise<void>} resolve quando o modal é fechado
 */
export function messageModal({
  title = 'Aviso',
  message = '',
  tone = 'info',
  icon = null,
  progress = null,
  confirmLabel = 'Entendi',
  dismissible = true,
} = {}) {
  return new Promise((resolve) => {
    const colors = TONE_COLORS[tone] || TONE_COLORS.info;
    // `icon` (SVG path custom) sobrepõe o ícone do tom — ex.: símbolo de IA.
    const iconPath = icon || TONE_ICONS[tone] || TONE_ICONS.info;

    // Barra de progresso opcional (0..1). O gradiente (roxo no light, bronze
    // no dark) vem de `.msgmodal-progress-fill` em components/modal.css.
    const pct = (typeof progress === 'number' && Number.isFinite(progress))
      ? Math.max(0, Math.min(100, Math.round(progress * 100)))
      : null;
    const progressHtml = pct == null ? '' : `
          <div class="msgmodal-progress" role="progressbar"
               aria-valuenow="${pct}" aria-valuemin="0" aria-valuemax="100">
            <span class="msgmodal-progress-fill" style="width:${pct}%"></span>
          </div>`;

    const overlay = document.createElement('div');
    overlay.className = 'modal-overlay';
    overlay.style.zIndex = '2200'; // acima de modais comuns (newTripModal etc.)

    overlay.innerHTML = `
      <div class="modal modal--narrow" role="alertdialog" aria-modal="true"
           aria-labelledby="msgmodal-title" aria-describedby="msgmodal-msg">
        <div class="modal__body" style="align-items:center;text-align:center;padding-top:30px;gap:14px">
          <span aria-hidden="true" style="
            display:flex;align-items:center;justify-content:center;
            width:56px;height:56px;border-radius:var(--r-full, 999px);
            background:${colors.bg};color:${colors.fg}">
            <svg width="26" height="26" viewBox="0 0 24 24" fill="none"
                 stroke="currentColor" stroke-width="2"
                 stroke-linecap="round" stroke-linejoin="round">${iconPath}</svg>
          </span>
          <h3 id="msgmodal-title" style="
            margin:0;font-family:var(--font);font-size:var(--text-lg);
            font-weight:700;color:var(--text-primary)">${esc(title)}</h3>
          <p id="msgmodal-msg" style="
            margin:0;color:var(--text-secondary);font-size:var(--text-sm);
            line-height:1.5;white-space:pre-line">${esc(message)}</p>
          ${progressHtml}
        </div>
        <div class="modal__footer" style="justify-content:center">
          <button type="button" class="btn btn-primary" data-action="ok"
                  style="min-width:160px">${esc(confirmLabel)}</button>
        </div>
      </div>
    `;

    function close() {
      document.removeEventListener('keydown', onKey);
      overlay.remove();
      resolve();
    }
    function onKey(e) {
      if (e.key === 'Escape' && dismissible) close();
    }

    overlay.querySelector('[data-action="ok"]').addEventListener('click', close);
    overlay.addEventListener('click', (e) => {
      if (e.target === overlay && dismissible) close();
    });
    document.addEventListener('keydown', onKey);

    document.body.appendChild(overlay);
    overlay.querySelector('[data-action="ok"]').focus();
  });
}
